import React, { useState } from "react";
import { api, imageUrl, formatApiError } from "../lib/api";
import { Sparkle, X, ArrowsClockwise, Check, Lightning, FloppyDisk } from "@phosphor-icons/react";

/* ---- Prompt presets ---- */
const HERO_PRESETS = [
  {
    label: "Signature",
    prompt:
      "Editorial abstract illustration of South Africa's mobile network topology — glowing rainbow-gradient signal traces (sky blue #55a2d2, magenta #af3fa6, gold #dda632) flowing between softly rendered cell towers, fibre nodes and handsets on a deep navy canvas. Generous negative space, no text, 16:9 landscape, premium editorial.",
  },
  {
    label: "Mobile-first",
    prompt:
      "Premium editorial illustration: a modern South African business team with smartphones, laptops and tablets connected by rainbow-gradient arcs (sky blue, magenta, gold) over a deep-navy background. Warm, human, no text, 16:9 landscape.",
  },
  {
    label: "Fibre door",
    prompt:
      "Minimal editorial illustration of a fibre cable with glowing rainbow light flowing into a modern office building against a soft deep-navy sky. Abstract, premium, no text, 16:9 landscape.",
  },
];

const SEASONAL_PRESETS = [
  { label: "Spring", prompt: "Editorial illustration in soft spring colours (pink, sage, gold) of South Africa's mobile and fibre network as glowing rainbow filaments flowing through blooming jacaranda trees and sunlit savannah, deep navy background, 16:9 landscape, no text, premium editorial." },
  { label: "Summer", prompt: "Editorial illustration of an azure summer sky over the Drakensberg with rainbow signal arcs (sky blue, magenta, gold) connecting beach umbrellas, smartphones and cloud icons, deep navy background, 16:9 landscape, no text, premium editorial." },
  { label: "Autumn", prompt: "Editorial illustration of autumn leaves, copper-and-gold light, and rainbow fibre lines flowing through a quiet South African vineyard at golden hour, deep navy background, 16:9 landscape, no text, premium editorial." },
  { label: "Winter", prompt: "Editorial illustration of a crisp blue winter dawn with frosted aloes and rainbow gradient signal arcs (icy blue, magenta, gold) crossing a deep navy night sky over Cape Town, 16:9 landscape, no text, premium editorial." },
  { label: "Festive", prompt: "Editorial illustration of a festive South African summer with warm gold lights, rainbow ribbons, gift boxes, and signal arcs flowing between handsets, deep navy background, 16:9 landscape, no text, premium editorial." },
];

const SECTION_PRESETS = [
  { label: "Connectivity", slot: "pillar_connectivity", prompt: "Square editorial 1:1 illustration: glowing rainbow-gradient fibre cables, cell towers and 5G signal arcs over a deep navy sky, no text, premium editorial." },
  { label: "Cloud & IT", slot: "pillar_cloud", prompt: "Square editorial 1:1 illustration: stylised cloud icons, server racks and rainbow data streams (sky blue, magenta, gold) flowing across a deep navy background, no text, premium editorial." },
  { label: "Smart Business", slot: "pillar_smart", prompt: "Square editorial 1:1 illustration: IoT sensors, dashcams, CCTV cameras and AI nodes interconnected by rainbow gradient lines on deep navy, no text, premium editorial." },
  { label: "Build Package", slot: "build_package", prompt: "Square editorial 1:1 illustration: a sliding control panel with rainbow gradient sliders, SIM cards, fibre icons and a smartphone, deep navy background, no text, premium editorial." },
];

const TARGETS = [
  { key: "hero", label: "Homepage hero" },
  { key: "logo", label: "Brand logo" },
  { key: "pillar_connectivity", label: "Connectivity pillar" },
  { key: "pillar_cloud", label: "Cloud & IT pillar" },
  { key: "pillar_smart", label: "Smart Business pillar" },
  { key: "build_package", label: "Build-Package illustration" },
];

export default function NanoBananaModal({ open, onClose, onGenerated }) {
  const [prompt, setPrompt] = useState(HERO_PRESETS[0].prompt);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [savingTo, setSavingTo] = useState(null);

  if (!open) return null;

  const generate = async () => {
    if (prompt.trim().length < 10) {
      setErr("Prompt needs at least 10 characters.");
      return;
    }
    setErr("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/generate-image", { prompt });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const saveToSlot = async (slotKey) => {
    if (!result) return;
    setSavingTo(slotKey);
    try {
      await api.put("/brand", { [`${slotKey}_path`]: result.path });
      onGenerated?.({ slot: slotKey, path: result.path });
      close();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSavingTo(null);
    }
  };

  const saveAsSlide = async () => {
    if (!result) return;
    setSavingTo("slide");
    try {
      await api.post("/slides", {
        title: "Generated Hero",
        subtitle: "Created with Nano Banana",
        cta_label: "Explore",
        cta_link: "/offers",
        image_path: result.path,
        order: 0,
        active: true,
      });
      onGenerated?.({ slot: "slide", path: result.path });
      close();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSavingTo(null);
    }
  };

  const close = () => {
    setResult(null);
    setErr("");
    setPrompt(HERO_PRESETS[0].prompt);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
      onClick={close}
      data-testid="nano-banana-modal"
    >
      <div
        className="bg-[#141e32] rounded-[32px] w-full max-w-3xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-8 border-b border-[#2c3854]">
          <div>
            <div className="eyebrow mb-2 text-[#af3fa6]">AI Image Studio</div>
            <h2 className="font-display text-3xl text-[#f5ecd9]">
              Nano <span className="rainbow-text">Banana.</span>
            </h2>
            <p className="mt-2 text-sm text-[#a8b2cc] max-w-md">
              Describe the visual — pick a preset, regenerate seasonally, or craft a section
              illustration. Save the result to any brand slot.
            </p>
          </div>
          <button
            onClick={close}
            className="p-2 rounded-full hover:bg-[#070b14]"
            data-testid="nano-banana-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <PresetGroup label="Hero" testidPrefix="hero" presets={HERO_PRESETS} setPrompt={setPrompt} />
          <PresetGroup label="Seasonal Hero" testidPrefix="season" presets={SEASONAL_PRESETS} setPrompt={setPrompt} accent="#dda632" />
          <PresetGroup label="Section Illustrations" testidPrefix="section" presets={SECTION_PRESETS} setPrompt={setPrompt} accent="#55a2d2" />

          <label className="block">
            <span className="block eyebrow mb-2">Prompt</span>
            <textarea
              rows={5}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="nano-banana-prompt"
              className="w-full rounded-2xl border border-[#2c3854] focus:border-[#f5ecd9] outline-none p-4 resize-none bg-[#1c2942]/60"
            />
          </label>

          {err && (
            <div className="rounded-2xl border border-[#af3fa6] text-[#af3fa6] bg-[#1c2942] p-3 text-sm" data-testid="nano-banana-error">
              {err}
            </div>
          )}

          {result && (
            <div className="space-y-5" data-testid="nano-banana-result">
              <div className="relative rounded-[24px] overflow-hidden border border-[#2c3854] bg-[#070b14]">
                <img src={imageUrl(result.path)} alt="generated" className="w-full" />
              </div>
              <div>
                <div className="eyebrow mb-3">Save to brand slot</div>
                <div className="flex flex-wrap gap-2">
                  {TARGETS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => saveToSlot(t.key)}
                      disabled={savingTo !== null}
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.16em] font-nav font-semibold rounded-full border border-[#2c3854] text-[#f5ecd9] hover:border-[#f5ecd9] hover:bg-[#070b14] disabled:opacity-50"
                      data-testid={`nano-save-${t.key}`}
                    >
                      <FloppyDisk size={13} weight="bold" />
                      {savingTo === t.key ? "Saving…" : t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2 border-t border-[#2c3854]">
                <button
                  onClick={saveAsSlide}
                  disabled={savingTo !== null}
                  className="btn-outline disabled:opacity-60"
                  data-testid="nano-banana-save-slide"
                >
                  <Lightning size={14} weight="bold" /> Save as new slide
                </button>
                <button
                  onClick={generate}
                  disabled={loading || savingTo !== null}
                  className="btn-outline disabled:opacity-60"
                  data-testid="nano-banana-regenerate"
                >
                  <ArrowsClockwise size={14} weight="bold" /> Try another
                </button>
              </div>
            </div>
          )}

          {!result && (
            <div className="pt-2">
              <button
                onClick={generate}
                disabled={loading}
                className="btn-primary disabled:opacity-60"
                data-testid="nano-banana-generate"
              >
                <Sparkle size={14} weight="fill" />
                {loading ? "Generating — ~8s" : "Generate image"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PresetGroup({ label, testidPrefix, presets, setPrompt, accent }) {
  return (
    <div>
      <div className="eyebrow mb-3" style={accent ? { color: accent } : {}}>
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => setPrompt(p.prompt)}
            className="px-4 py-2 text-xs uppercase tracking-[0.16em] font-nav font-semibold rounded-full border border-[#2c3854] text-[#f5ecd9] hover:border-[#f5ecd9] hover:bg-[#070b14]"
            data-testid={`nano-${testidPrefix}-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
